<template>
  <PageChrome>
    <template #title>ICP and Personas</template>

    <template #actions>
      <template v-if="activeTab === 'personas'">
        <button class="btn" type="button" @click="exportCsv">Export CSV</button>
        <label class="btn" for="personaCsvInput">
          <input id="personaCsvInput" type="file" accept=".csv" style="display:none" @change="onCsv" />
          Import CSV
        </label>
        <button class="btn" type="button" @click="openCreateModal">Create Persona</button>
      </template>
      <template v-else>
        <button class="btn" type="button" :disabled="icpLearning" @click="runIcpAnalyst">
          {{ icpLearning ? 'Running…' : 'Run ICP Analyst' }}
        </button>
        <button class="btn primary" type="button" :disabled="icpSaving" @click="saveIcpDefinition">
          {{ icpSaving ? 'Saving…' : 'Save' }}
        </button>
      </template>
    </template>

    <div class="tabBar">
      <button
        class="tabBtn"
        :class="{ active: activeTab === 'personas' }"
        type="button"
        @click="activeTab = 'personas'"
      >Personas</button>
      <button
        class="tabBtn"
        :class="{ active: activeTab === 'icp' }"
        type="button"
        @click="activeTab = 'icp'"
      >ICP Definition</button>
      <button
        class="tabBtn"
        :class="{ active: activeTab === 'learning' }"
        type="button"
        @click="activeTab = 'learning'; loadFeedbackQueue()"
      >
        Learning
        <span v-if="feedbackUnprocessedTotal > 0" class="learningBadge">{{ feedbackUnprocessedTotal }}</span>
      </button>
    </div>

    <div v-if="error" class="error">{{ error }}</div>
    <div v-if="success" class="success">{{ success }}</div>

    <!-- ===== PERSONAS TAB ===== -->
    <template v-if="activeTab === 'personas'">
    <div class="list-panel personaPanel">
      <div class="list-panel-header">
        <div>
          <div class="list-panel-title">Personas <span class="muted">({{ personas.length }})</span></div>
          <div class="panelSubtitle">Order controls swimlane rows and account-list persona columns. About Us fields are managed on the About Us page.</div>
        </div>
      </div>

      <div v-if="loading" class="list-panel-empty">Loading personas…</div>
      <div v-else-if="personas.length === 0" class="list-panel-empty">No personas yet.</div>
      <div v-else class="tableWrap">
        <table class="personaTable">
          <thead>
            <tr>
              <th class="orderCol">Order</th>
              <th>Name</th>
              <th>Explicit titles</th>
              <th>Titles to exclude</th>
              <th>Description</th>
              <th class="actionsCol">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(persona, index) in personas" :key="persona.localKey" class="personaRow" @click="openEditModal(index)">
              <td class="orderCol" @click.stop>
                <div class="orderButtons">
                  <button class="iconBtn" type="button" :disabled="index === 0 || reordering" title="Move up" @click="movePersona(index, -1)">
                    <HeroIcon name="ChevronUpIcon" iconStyle="outline" :size="16" />
                  </button>
                  <button class="iconBtn" type="button" :disabled="index === personas.length - 1 || reordering" title="Move down" @click="movePersona(index, 1)">
                    <HeroIcon name="ChevronDownIcon" iconStyle="outline" :size="16" />
                  </button>
                </div>
              </td>
              <td>
                <div class="cellPrimary">{{ persona.name || 'Untitled persona' }}</div>
              </td>
              <td>
                <div class="cellValue cellTruncate">{{ persona.explicitTitlesText || '—' }}</div>
              </td>
              <td>
                <div class="cellValue cellTruncate">{{ persona.titlesToExcludeText || '—' }}</div>
              </td>
              <td>
                <div class="cellValue cellClamp">{{ persona.description || '—' }}</div>
              </td>
              <td class="actionsCol">
                <button class="btn small" type="button" @click.stop="openEditModal(index)">Edit</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    </template><!-- end personas tab -->

    <!-- ===== ICP DEFINITION TAB ===== -->
    <template v-if="activeTab === 'icp'">
      <div class="list-panel icpPanel">
        <div class="list-panel-header">
          <div>
            <div class="list-panel-title">ICP Definition
              <span v-if="icpDefinition" class="muted"> (v{{ icpDefinition.version }})</span>
            </div>
            <div class="panelSubtitle">
              Define your ideal customer profile. Agents use this when assessing whether to prospect an account.
              Use "Run ICP Analyst" to automatically derive the definition from your closed-won opportunities.
            </div>
          </div>
        </div>

        <div v-if="icpLoading" class="list-panel-empty">Loading…</div>
        <div v-else class="icpForm">
          <div class="icpSection">
            <div class="icpSectionTitle">Firmographics</div>
            <div class="icpSectionSubtitle">Budget, company size, structure, funding stage, geography.</div>
            <textarea
              v-model="icpDraft.can_buy_description"
              class="textarea"
              rows="4"
              placeholder="e.g. SaaS companies with 50–500 employees, Series A–C, primarily in North America, with a dedicated sales team of 5+ reps."
            />
          </div>

          <div class="icpSection">
            <div class="icpSectionTitle">Timing</div>
            <div class="icpSectionSubtitle">Urgency indicators, pain points, timing signals.</div>
            <textarea
              v-model="icpDraft.needs_to_buy_description"
              class="textarea"
              rows="4"
              placeholder="e.g. Actively scaling outbound, recently hired new VP Sales, posting for multiple AE roles, or experiencing pipeline coverage pressure."
            />
          </div>

          <div class="icpSection">
            <div class="icpSectionTitle">Product Fit</div>
            <div class="icpSectionSubtitle">Values alignment, tech stack compatibility, cultural fit.</div>
            <textarea
              v-model="icpDraft.wants_approach_description"
              class="textarea"
              rows="4"
              placeholder="e.g. Companies that value AI-assisted workflows, are willing to give tools CRM access, and are transitioning from founder-led to a repeatable sales process."
            />
          </div>

        </div>
      </div>

      <!-- Version history -->
      <div v-if="icpHistory.length > 1" class="list-panel icpHistoryPanel">
        <div class="list-panel-header">
          <div class="list-panel-title">Version History</div>
        </div>
        <table class="personaTable">
          <thead>
            <tr>
              <th>Version</th>
              <th>Source</th>
              <th>Summary</th>
              <th>Saved</th>
              <th class="actionsCol">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="v in icpHistory" :key="v.version">
              <td>v{{ v.version }}<span v-if="v.version === icpDefinition?.version" class="icpCurrentBadge">current</span></td>
              <td>{{ v.source }}</td>
              <td><div class="cellClamp">{{ v.change_summary || '—' }}</div></td>
              <td>{{ formatDate(v.created_at) }}</td>
              <td class="actionsCol" style="white-space:nowrap; display:flex; gap:6px; flex-wrap:wrap; justify-content:flex-end">
                <button
                  v-if="v.version > 1"
                  class="btn small"
                  type="button"
                  @click="openIcpDiff(v.version - 1, v.version)"
                >Diff vs previous</button>
                <button
                  v-if="v.version !== icpDefinition?.version"
                  class="btn small"
                  type="button"
                  :disabled="icpSaving"
                  @click="restoreVersion(v)"
                >Restore</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- ICP Diff Modal -->
      <Modal
        v-if="icpDiffModal"
        :title="`ICP Definition: v${icpDiffModal.from_version} → v${icpDiffModal.to_version}`"
        :max-width="700"
        @close="icpDiffModal = null"
      >
        <div v-if="icpDiffLoading" class="list-panel-empty">Loading diff…</div>
        <div v-else-if="icpDiffModal.changes.length === 0" class="list-panel-empty muted">No changes between these versions.</div>
        <div v-else class="icpDiffBody">
          <div v-for="change in icpDiffModal.changes" :key="change.path" class="icpDiffItem">
            <div class="icpDiffPath">{{ change.path }}</div>
            <template v-if="typeof change.before === 'string' && typeof change.after === 'string'">
              <LineDiff :before="change.before" :after="change.after" left-label="Before" right-label="After" />
            </template>
            <template v-else>
              <div class="icpDiffValues">
                <div class="icpDiffBefore">{{ change.before ?? '(none)' }}</div>
                <div class="icpDiffArrow">→</div>
                <div class="icpDiffAfter">{{ change.after ?? '(none)' }}</div>
              </div>
            </template>
          </div>
        </div>
        <template #footer>
          <div class="modalFooterRow">
            <div class="modalFooterActions">
              <button class="btn" type="button" @click="icpDiffModal = null">Close</button>
            </div>
          </div>
        </template>
      </Modal>

    </template><!-- end icp tab -->

    <!-- ===== LEARNING TAB ===== -->
    <template v-if="activeTab === 'learning'">
      <div class="list-panel learningPanel">
        <div class="list-panel-header">
          <div>
            <div class="list-panel-title">ICP Feedback Queue</div>
            <div class="panelSubtitle">
              The ICP learns from two sources: manual score corrections (when you edit an account's ICP score) and
              Account Finder rejections (when you decline a staged account with a reason).
              A new ICP definition version is automatically generated once {{ feedbackQueue?.threshold ?? 5 }} unprocessed signals accumulate.
            </div>
          </div>
        </div>

        <div v-if="feedbackLoading" class="list-panel-empty">Loading…</div>
        <template v-else-if="feedbackQueue">
          <!-- Queue stats -->
          <div class="learningStats">
            <div class="learningStat">
              <span class="learningStatValue">{{ feedbackQueue.unprocessed_dismissal_count }}</span>
              <span class="learningStatLabel">unprocessed dismissals</span>
            </div>
            <div class="learningStat">
              <span class="learningStatValue">{{ feedbackQueue.unprocessed_correction_count }}</span>
              <span class="learningStatLabel">unprocessed corrections</span>
            </div>
            <div class="learningStat">
              <span class="learningStatValue">{{ feedbackQueue.threshold }}</span>
              <span class="learningStatLabel">needed to trigger</span>
            </div>
          </div>

          <!-- Sales-play candidate dismissals -->
          <div class="learningSection">
            <div class="learningSectionTitle">Dismissed Sales-Play Candidates</div>
            <div v-if="feedbackQueue.dismissed_candidates.length === 0" class="muted">No dismissals yet.</div>
            <table v-else class="personaTable">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Domain</th>
                  <th>Category</th>
                  <th>Detail</th>
                  <th>Dismissed</th>
                  <th>Processed</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="d in feedbackQueue.dismissed_candidates" :key="d.id">
                  <td>{{ d.name || '—' }}</td>
                  <td class="muted">{{ d.domain || '—' }}</td>
                  <td>{{ d.reason_category || '—' }}</td>
                  <td class="cellTruncate">{{ d.reason_text || '—' }}</td>
                  <td class="muted">{{ d.dismissed_at ? new Date(d.dismissed_at).toLocaleDateString() : '—' }}</td>
                  <td>
                    <span v-if="d.processed" class="processedBadge">Processed</span>
                    <span v-else class="pendingBadge">Pending</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- ICP Score Corrections -->
          <div class="learningSection">
            <div class="learningSectionTitle">ICP Score Corrections</div>
            <div v-if="feedbackQueue.corrections.length === 0" class="muted">No corrections yet.</div>
            <table v-else class="personaTable">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Source</th>
                  <th>Corrected</th>
                  <th>Processed</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="c in feedbackQueue.corrections" :key="c.id">
                  <td class="muted">{{ c.account_id.slice(0, 8) }}…</td>
                  <td>{{ c.source }}</td>
                  <td class="muted">{{ c.corrected_at ? new Date(c.corrected_at).toLocaleDateString() : '—' }}</td>
                  <td>
                    <span v-if="c.processed" class="processedBadge">Processed</span>
                    <span v-else class="pendingBadge">Pending</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>
    </template><!-- end learning tab -->

    <Modal v-if="editorState" :title="editorState.mode === 'create' ? 'Create Persona' : 'Edit Persona'" :error="editorError" @close="closeEditor">
      <div class="dialogFormGrid">
        <div class="dialogFormField">
          <label>Name</label>
          <input v-model="editorState.draft.name" class="input" placeholder="Sales" />
        </div>

        <div class="dialogFormField dialogFormFieldSpan2">
          <label>Description</label>
          <textarea v-model="editorState.draft.description" class="textarea" rows="3" placeholder="How should this persona be interpreted?" />
        </div>

        <div class="dialogFormField dialogFormFieldSpan2">
          <label>Title Examples To Include</label>
          <textarea v-model="editorState.draft.explicitTitlesText" class="textarea" rows="3" placeholder="VP Sales&#10;Head of Revenue Operations" />
          <div class="muted">One title per line. These are positive title examples for this persona.</div>
        </div>

        <div class="dialogFormField dialogFormFieldSpan2">
          <label>Title Snippets To Exclude</label>
          <textarea v-model="editorState.draft.titlesToExcludeText" class="textarea" rows="3" placeholder="VP, Growth Marketing&#10;Director, Demand Generation" />
          <div class="muted">One phrase per line. If a contact title contains any of these phrases, they are globally excluded from automated buying-team and prospect targeting.</div>
        </div>
      </div>

      <template #footer>
        <div class="modalFooterRow">
          <div class="modalFooterDanger">
            <button v-if="editorState.mode === 'edit'" class="btn danger" type="button" :disabled="saving" @click="openDeletePromptFromEditor">Delete</button>
          </div>
          <div class="modalFooterActions">
            <button class="btn" type="button" :disabled="saving" @click="closeEditor">Cancel</button>
            <button class="btn primary" type="button" :disabled="saving" @click="saveEditor">{{ saving ? 'Saving…' : editorState.mode === 'create' ? 'Create' : 'Save' }}</button>
          </div>
        </div>
      </template>
    </Modal>

    <Modal
      v-if="deleteState"
      title="Delete persona"
      :max-width="520"
      :body-min-height="deleteUsageCount > 0 && !deleteUsageLoading ? 220 : 120"
      @close="closeDeletePrompt"
    >
      <div class="deleteContent">
        <div class="muted">
          <template v-if="deleteState.persistedId && deleteUsageLoading">
            Checking whether this persona is in use…
          </template>
          <template v-else-if="deleteState.persistedId && deleteUsageCount > 0">
            This persona is in use by {{ deleteUsageCount }} buying team member{{ deleteUsageCount === 1 ? '' : 's' }}, please choose a replacement persona.
          </template>
          <template v-else-if="deleteState.persistedId">
            This persona is not in use, so you can proceed to delete.
          </template>
          <template v-else>
            This unsaved persona will be removed from the draft.
          </template>
        </div>

        <div v-if="deleteState.persistedId && !deleteUsageLoading && deleteUsageCount > 0" class="field">
          <label>Replacement persona</label>
          <SearchSelect
            v-model="deleteState.replacementPersonaId"
            :options="replacementPersonaOptions"
            placeholder="Select replacement"
            search-placeholder="Search personas…"
            clear-label="Select replacement"
            :allow-clear="true"
            :max-height="220"
          />
        </div>
      </div>

      <template #footer>
        <div class="modalFooterRow deleteFooterRow">
          <div class="modalFooterActions">
            <button class="btn" type="button" @click="closeDeletePrompt">Cancel</button>
            <button class="btn danger" type="button" :disabled="deletingPersona" @click="confirmDeletePersona">{{ deletingPersona ? 'Deleting…' : 'Delete' }}</button>
          </div>
        </div>
      </template>
    </Modal>
  </PageChrome>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import PageChrome from '../components/PageChrome.vue'
import HeroIcon from '../../components/HeroIcon.vue'
import LineDiff from '../../components/LineDiff.vue'
import Modal from '../../components/Modal.vue'
import SearchSelect from '../../components/SearchSelect.vue'
import { apiFetch } from '../../services/api'
import { useChatScope } from '../composables/useChatScope'

const { setScope, clearScope } = useChatScope()

// ---------------------------------------------------------------------------
// Tab state
// ---------------------------------------------------------------------------
const activeTab = ref<'personas' | 'icp' | 'learning'>('personas')

// ---------------------------------------------------------------------------
// ICP Definition state
// ---------------------------------------------------------------------------

type ICPDefinition = {
  id: string
  version: number
  can_buy_description: string
  needs_to_buy_description: string
  wants_approach_description: string
  can_buy_criteria: Record<string, unknown>
  needs_to_buy_criteria: Record<string, unknown>
  wants_approach_criteria: Record<string, unknown>

  source: string
  change_summary: string
  created_at: string
}

const icpDefinition = ref<ICPDefinition | null>(null)
const icpHistory = ref<ICPDefinition[]>([])
const icpLoading = ref(false)
const icpSaving = ref(false)
const icpLearning = ref(false)

type ICPDiffChange = { path: string; before: unknown; after: unknown }
type ICPDiffModal = { from_version: number; to_version: number; changes: ICPDiffChange[] }
const icpDiffModal = ref<ICPDiffModal | null>(null)
const icpDiffLoading = ref(false)

async function openIcpDiff(fromVersion: number, toVersion: number) {
  icpDiffModal.value = { from_version: fromVersion, to_version: toVersion, changes: [] }
  icpDiffLoading.value = true
  try {
    const resp = await apiFetch(`/icp/definition/diff?from_version=${fromVersion}&to_version=${toVersion}`)
    icpDiffModal.value = {
      from_version: fromVersion,
      to_version: toVersion,
      changes: resp?.changes ?? [],
    }
  } catch (e: any) {
    error.value = e?.message || 'Failed to load diff'
    icpDiffModal.value = null
  } finally {
    icpDiffLoading.value = false
  }
}
const icpDraft = ref({
  can_buy_description: '',
  needs_to_buy_description: '',
  wants_approach_description: '',
})

function populateDraftFromDefinition(def: ICPDefinition | null) {
  icpDraft.value = {
    can_buy_description: def?.can_buy_description ?? '',
    needs_to_buy_description: def?.needs_to_buy_description ?? '',
    wants_approach_description: def?.wants_approach_description ?? '',
  }
}

async function loadIcpDefinition() {
  icpLoading.value = true
  error.value = ''
  try {
    const [defResp, histResp] = await Promise.all([
      apiFetch('/icp/definition'),
      apiFetch('/icp/definition/history'),
    ])
    icpDefinition.value = defResp?.definition ?? null
    icpHistory.value = histResp?.history ?? []
    populateDraftFromDefinition(icpDefinition.value)
  } catch (e: any) {
    error.value = e?.message || 'Failed to load ICP definition'
  } finally {
    icpLoading.value = false
  }
}

async function saveIcpDefinition() {
  icpSaving.value = true
  error.value = ''
  success.value = ''
  try {
    const resp = await apiFetch('/icp/definition', {
      method: 'PUT',
      body: {
        can_buy_description: icpDraft.value.can_buy_description,
        needs_to_buy_description: icpDraft.value.needs_to_buy_description,
        wants_approach_description: icpDraft.value.wants_approach_description,
        change_summary: 'Manual edit',
      },
    })
    icpDefinition.value = resp?.definition ?? null
    await loadIcpDefinition()
    success.value = 'ICP definition saved'
  } catch (e: any) {
    error.value = e?.message || 'Failed to save ICP definition'
  } finally {
    icpSaving.value = false
  }
}

// ---------------------------------------------------------------------------
// Learning tab
// ---------------------------------------------------------------------------

type FeedbackQueue = {
  threshold: number
  unprocessed_dismissal_count: number
  unprocessed_correction_count: number
  dismissed_candidates: Array<{
    id: string
    domain: string | null
    name: string | null
    reason_category: string | null
    reason_text: string | null
    dismissed_at: string | null
    processed: boolean
    processed_at: string | null
  }>
  corrections: Array<{
    id: string
    account_id: string
    source: string
    corrected_at: string | null
    processed: boolean
    processed_at: string | null
  }>
}

const feedbackQueue = ref<FeedbackQueue | null>(null)
const feedbackLoading = ref(false)
const feedbackUnprocessedTotal = computed(() =>
  feedbackQueue.value
    ? feedbackQueue.value.unprocessed_dismissal_count + feedbackQueue.value.unprocessed_correction_count
    : 0
)

async function loadFeedbackQueue() {
  if (feedbackLoading.value) return
  feedbackLoading.value = true
  try {
    feedbackQueue.value = await apiFetch('/icp/feedback-queue')
  } catch {
    // non-blocking; Learning tab just shows empty
  } finally {
    feedbackLoading.value = false
  }
}

async function runIcpAnalyst() {
  icpLearning.value = true
  error.value = ''
  success.value = ''
  try {
    const resp = await apiFetch('/icp/learn', { method: 'POST' })
    icpDefinition.value = resp?.definition ?? null
    await loadIcpDefinition()
    success.value = 'ICP Analyst complete — definition updated'
  } catch (e: any) {
    error.value = e?.message || 'ICP Analyst failed — make sure closed-won Salesforce sync has run'
  } finally {
    icpLearning.value = false
  }
}

async function restoreVersion(v: ICPDefinition) {
  icpSaving.value = true
  error.value = ''
  success.value = ''
  try {
    const resp = await apiFetch('/icp/definition', {
      method: 'PUT',
      body: {
        can_buy_description: v.can_buy_description,
        needs_to_buy_description: v.needs_to_buy_description,
        wants_approach_description: v.wants_approach_description,
        can_buy_criteria: v.can_buy_criteria,
        needs_to_buy_criteria: v.needs_to_buy_criteria,
        wants_approach_criteria: v.wants_approach_criteria,
        change_summary: `Restored from v${v.version}`,
      },
    })
    icpDefinition.value = resp?.definition ?? null
    await loadIcpDefinition()
    success.value = `Restored to v${v.version}`
  } catch (e: any) {
    error.value = e?.message || 'Failed to restore version'
  } finally {
    icpSaving.value = false
  }
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  } catch {
    return iso
  }
}

watch(activeTab, (tab) => {
  if (tab === 'icp' && !icpDefinition.value && !icpLoading.value) {
    void loadIcpDefinition()
  }
})

type PersonaDraft = {
  id: string | null
  localKey: string
  name: string
  description: string
  explicitTitlesText: string
  titlesToExcludeText: string
}

type DeleteState = {
  index: number
  persistedId: string | null
  replacementPersonaId: string
}

type EditorState = {
  mode: 'create' | 'edit'
  index: number
  draft: PersonaDraft
}

const error = ref('')
const success = ref('')
const loading = ref(false)
const saving = ref(false)
const reordering = ref(false)
const deletingPersona = ref(false)
const deleteUsageLoading = ref(false)
const deleteUsageCount = ref(0)
const deleteState = ref<DeleteState | null>(null)
const editorState = ref<EditorState | null>(null)
const editorError = ref('')
const nextLocalId = ref(1)

const personas = ref<PersonaDraft[]>([])

const replacementPersonaOptions = computed(() => {
  if (!deleteState.value) return []
  return personas.value
    .filter((persona, idx) => idx !== deleteState.value?.index && String(persona.name || '').trim())
    .map((persona) => ({ value: String(persona.id || ''), label: String(persona.name || '').trim() }))
    .filter((option) => option.value)
})

function makeLocalKey() {
  const value = nextLocalId.value
  nextLocalId.value += 1
  return `persona-${value}`
}

function normalizePersonas(rows: any[]): PersonaDraft[] {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    id: row?.id ? String(row.id) : null,
    localKey: makeLocalKey(),
    name: String(row?.name || ''),
    description: String(row?.description || ''),
    explicitTitlesText: Array.isArray(row?.explicit_titles) ? row.explicit_titles.join('\n') : '',
    titlesToExcludeText: Array.isArray(row?.titles_to_exclude) ? row.titles_to_exclude.join('\n') : '',
  }))
}

async function reload() {
  loading.value = true
  error.value = ''
  success.value = ''
  deleteState.value = null
  try {
    const resp = await apiFetch('/settings/icp_personas')
    personas.value = normalizePersonas(resp?.personas || [])
  } catch (e: any) {
    error.value = e?.message || 'Failed'
  } finally {
    loading.value = false
  }
}

async function onCsv(evt: Event) {
  const target = evt.target as HTMLInputElement | null
  const file = target?.files?.[0]
  if (target) target.value = ''
  if (!file) return

  error.value = ''
  success.value = ''
  try {
    const data = await apiFetch('/api/settings/icp_personas/import_csv', {
      method: 'POST',
      body: (() => {
        const fd = new FormData()
        fd.append('file', file)
        return fd
      })(),
    })

    personas.value = normalizePersonas(data?.personas || [])
    success.value = 'Personas imported'
  } catch (e: any) {
    error.value = e?.message || 'Failed'
  }
}

async function exportCsv() {
  error.value = ''
  success.value = ''
  try {
    const blob = await apiFetch('/api/settings/icp_personas/export_csv', {
      responseType: 'blob',
    })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'personas.csv'
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  } catch (e: any) {
    error.value = e?.message || 'Failed'
  }
}

function makeEmptyDraft(): PersonaDraft {
  return {
    id: null,
    localKey: makeLocalKey(),
    name: '',
    description: '',
    explicitTitlesText: '',
    titlesToExcludeText: '',
  }
}

function openCreateModal() {
  editorError.value = ''
  editorState.value = {
    mode: 'create',
    index: personas.value.length,
    draft: makeEmptyDraft(),
  }
}

function openEditModal(index: number) {
  const persona = personas.value[index]
  if (!persona) return
  editorError.value = ''
  editorState.value = {
    mode: 'edit',
    index,
    draft: {
      id: persona.id,
      localKey: persona.localKey,
      name: persona.name,
      description: persona.description,
      explicitTitlesText: persona.explicitTitlesText,
      titlesToExcludeText: persona.titlesToExcludeText,
    },
  }
}

function closeEditor() {
  editorState.value = null
  editorError.value = ''
}

function buildPayload(source: PersonaDraft[]) {
  return {
    personas: source.map((persona) => ({
      id: persona.id || undefined,
      name: String(persona.name || '').trim(),
      description: String(persona.description || ''),
      explicit_titles: String(persona.explicitTitlesText || '')
        .split(/\r?\n/)
        .map((item) => item.trim())
        .filter(Boolean),
      titles_to_exclude: String(persona.titlesToExcludeText || '')
        .split(/\r?\n/)
        .map((item) => item.trim())
        .filter(Boolean),
    })),
  }
}

async function persistPersonas(nextPersonas: PersonaDraft[], successMessage = 'Saved') {
  saving.value = true
  error.value = ''
  success.value = ''
  try {
    const resp = await apiFetch('/settings/icp_personas', {
      method: 'PUT',
      body: buildPayload(nextPersonas),
    })
    personas.value = normalizePersonas(resp?.personas || [])
    success.value = successMessage
    return true
  } catch (e: any) {
    error.value = e?.message || 'Failed'
    return false
  } finally {
    saving.value = false
  }
}

async function movePersona(index: number, delta: number) {
  const nextIndex = index + delta
  if (nextIndex < 0 || nextIndex >= personas.value.length) return
  const clone = [...personas.value]
  const [item] = clone.splice(index, 1)
  clone.splice(nextIndex, 0, item)
  const previous = personas.value
  personas.value = clone
  reordering.value = true
  const ok = await persistPersonas(clone, 'Order saved')
  reordering.value = false
  if (!ok) personas.value = previous
}

async function promptDeletePersona(index: number) {
  const target = personas.value[index]
  if (!target) return
  deleteState.value = {
    index,
    persistedId: target.id,
    replacementPersonaId: '',
  }
  deleteUsageCount.value = 0
  if (!target.id) return
  deleteUsageLoading.value = true
  try {
    const resp = await apiFetch(`/settings/personas/${target.id}/usage`)
    deleteUsageCount.value = Number(resp?.buying_team_member_count || 0)
  } catch (e: any) {
    error.value = e?.message || 'Failed'
    closeDeletePrompt()
  } finally {
    deleteUsageLoading.value = false
  }
}

async function openDeletePromptFromEditor() {
  if (!editorState.value) return
  await promptDeletePersona(editorState.value.index)
}

function closeDeletePrompt() {
  deleteState.value = null
  deleteUsageLoading.value = false
  deleteUsageCount.value = 0
}

async function saveEditor() {
  const state = editorState.value
  if (!state) return
  const draft = {
    ...state.draft,
    name: String(state.draft.name || '').trim(),
    description: String(state.draft.description || ''),
    explicitTitlesText: String(state.draft.explicitTitlesText || ''),
    titlesToExcludeText: String(state.draft.titlesToExcludeText || ''),
  }
  if (!draft.name) {
    editorError.value = 'Persona name is required'
    return
  }
  const next = [...personas.value]
  if (state.mode === 'create') next.push(draft)
  else next.splice(state.index, 1, draft)
  editorError.value = ''
  const ok = await persistPersonas(next, state.mode === 'create' ? 'Persona created' : 'Persona updated')
  if (ok) closeEditor()
}

async function confirmDeletePersona() {
  const state = deleteState.value
  if (!state) return
  const target = personas.value[state.index]
  if (!target) {
    deleteState.value = null
    return
  }
  if (!state.persistedId) {
    personas.value.splice(state.index, 1)
    closeDeletePrompt()
    return
  }
  if (deleteUsageCount.value > 0 && !state.replacementPersonaId) {
    error.value = 'A replacement persona is required'
    return
  }
  deletingPersona.value = true
  error.value = ''
  success.value = ''
  try {
    await apiFetch(`/settings/personas/${state.persistedId}`, {
      method: 'DELETE',
      body: { replacement_persona_id: state.replacementPersonaId || null },
    })
    await reload()
    success.value = 'Persona deleted'
  } catch (e: any) {
    error.value = e?.message || 'Failed'
  } finally {
    deletingPersona.value = false
    closeDeletePrompt()
    closeEditor()
  }
}

onMounted(() => {
  setScope({ scopeType: 'workspace', scopeSubjectId: '', pageContext: null })
  reload()
})

onBeforeUnmount(() => {
  clearScope()
})
</script>

<style scoped>
.personaPanel {
  display: grid;
  gap: 0;
}

.muted,
.panelSubtitle {
  color: var(--muted);
  font-size: 13px;
}

.panelSubtitle {
  margin-top: 4px;
}

.tableWrap {
  overflow-x: auto;
}

.personaTable {
  width: 100%;
  border-collapse: collapse;
}

.personaTable th,
.personaTable td {
  padding: 14px 16px;
  border-top: 1px solid var(--border);
  text-align: left;
  vertical-align: middle;
}

.personaTable th {
  font-size: 12px;
  font-weight: 700;
  color: var(--muted);
  background: color-mix(in srgb, var(--surface) 92%, var(--field-bg) 8%);
}

.personaRow {
  cursor: pointer;
}

.personaRow:hover {
  background: color-mix(in srgb, var(--surface) 86%, var(--field-bg) 14%);
}

.orderCol {
  width: 88px;
}

.actionsCol {
  width: 96px;
  text-align: right;
}

.orderButtons {
  display: inline-flex;
  gap: 6px;
}

.iconBtn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.iconBtn:hover:not(:disabled) {
  background: var(--field-bg);
  color: var(--text);
}

.iconBtn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.cellPrimary {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
}

.cellValue {
  font-size: 13px;
  color: var(--text);
}

.cellTruncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 340px;
}

.cellClamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 420px;
}

.dialogFormGrid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.dialogFormField {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.dialogFormFieldSpan2 {
  grid-column: span 2;
}

.dialogFormField label,
.field label {
  font-size: 12px;
  font-weight: 700;
  color: var(--text);
}

.input,
.textarea,
select.input {
  width: 100%;
  border: 1px solid var(--border);
  background: var(--field-bg);
  color: var(--text);
  border-radius: 10px;
  padding: 10px;
  font-size: 14px;
}

.textarea {
  resize: vertical;
}

.list-panel-empty {
  padding: 18px 16px;
  color: var(--muted);
  font-size: 13px;
}

.modalFooterRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.modalFooterDanger,
.modalFooterActions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.error {
  margin-bottom: 10px;
  color: var(--danger);
}

.success {
  margin-top: 10px;
  color: var(--success);
}

.btn.small {
  padding: 6px 10px;
  font-size: 12px;
}

.deleteContent {
  display: grid;
  align-content: start;
  gap: 12px;
  min-height: 0;
  padding-bottom: 0;
}

.deleteFooterRow {
  width: 100%;
  justify-content: flex-end;
}

/* Tab bar */
.tabBar {
  display: flex;
  gap: 4px;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--border);
  padding-bottom: 0;
}

.tabBtn {
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: var(--muted);
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  cursor: pointer;
  border-radius: 0;
}

.tabBtn:hover {
  color: var(--text);
}

.tabBtn.active {
  color: var(--text);
  border-bottom-color: var(--accent);
}

/* ICP Definition tab */
.icpPanel {
  display: grid;
  gap: 0;
}

.icpForm {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px 20px 24px;
}

.icpSection {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.icpSectionTitle {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
}

.icpSectionSubtitle {
  font-size: 13px;
  color: var(--muted);
}

.icpSectionRow {
  flex-direction: row;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 20px;
}

.icpSectionField {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.icpSectionField label {
  font-size: 12px;
  font-weight: 700;
  color: var(--text);
}

.icpThresholdInput {
  width: 100px;
}

.icpHistoryPanel {
  margin-top: 16px;
}

/* Learning tab */
.learningPanel {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.learningStats {
  display: flex;
  gap: 32px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
}
.learningStat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.learningStatValue {
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
}
.learningStatLabel {
  font-size: 12px;
  color: var(--muted);
}
.learningSection {
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
}
.learningSectionTitle {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 10px;
}
.learningBadge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  background: var(--accent);
  color: white;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  vertical-align: middle;
}
.processedBadge {
  display: inline-block;
  padding: 1px 6px;
  background: var(--badge-success-bg, #d1fae5);
  color: var(--badge-success-text, #065f46);
  border-radius: 8px;
  font-size: 11px;
}
.pendingBadge {
  display: inline-block;
  padding: 1px 6px;
  background: var(--badge-warning-bg, #fef3c7);
  color: var(--badge-warning-text, #92400e);
  border-radius: 8px;
  font-size: 11px;
}

.icpCurrentBadge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 7px;
  background: color-mix(in srgb, var(--accent) 15%, transparent);
  color: var(--accent);
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.icpDiffBody {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.icpDiffItem {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.icpDiffPath {
  font-size: 11px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.icpDiffValues {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
}

.icpDiffBefore {
  flex: 1;
  color: var(--danger, #dc2626);
  background: color-mix(in srgb, var(--danger, #dc2626) 8%, transparent);
  border-radius: 4px;
  padding: 4px 8px;
  word-break: break-word;
}

.icpDiffAfter {
  flex: 1;
  color: var(--success, #16a34a);
  background: color-mix(in srgb, var(--success, #16a34a) 8%, transparent);
  border-radius: 4px;
  padding: 4px 8px;
  word-break: break-word;
}

.icpDiffArrow {
  color: var(--muted);
  padding-top: 4px;
  flex-shrink: 0;
}

@media (max-width: 900px) {
  .dialogFormGrid {
    grid-template-columns: minmax(0, 1fr);
  }

  .dialogFormFieldSpan2 {
    grid-column: auto;
  }
}
</style>
